import {createBrowserHistory} from 'history';
import {useDispatch} from 'react-redux';
import {Action, applyMiddleware, combineReducers, createStore, Store} from 'redux';
import {createReduxHistoryContext} from 'redux-first-history';
import {persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {createStateSyncMiddleware} from 'redux-state-sync';
import reduxThunk, {ThunkDispatch} from 'redux-thunk';
import {ExpandedKeysActionTypes} from '../persisted/expanded-keys/expandedKeysActions';
import {defaultState as defaultExpandedState} from '../persisted/expanded-keys/expandedKeysReducer';
import {GlobalActionTypes} from '../persisted/global/globalActions';
import {defaultState as defaultGlobalState} from '../persisted/global/globalReducer';
import {persistedReducer} from '../persisted/persistedReducer';
import {
    defaultState as defaultSystemUserManagementState
} from '../ui/pages/admin-panel/system-right-management/systemUserManagementReducer';
import {defaultState as defaultAPIState} from '../ui/pages/global/api-check/APIReducer';
import {defaultState as defaultStatusState} from '../ui/ui-state/local-status/localStatusReducer';
import {UIActions} from '../ui/ui-state/UIActions';
import {uiReducer} from '../ui/ui-state/UIReducer';
import {systemReducer} from '../resource/services/system/systemReducer';
import { defaultProjectUserManagementState } from '../ui/pages/project-user-management/projectUserManagementReducer';

// export const reduxBrowserHistory = createBrowserHistory();
// export const reduxHashHistory = createHashHistory();

export const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['persisted'],
};

const actionWhiteList = [
    'persist/FLUSH',
    'persist/PAUSE',
    'persist/PURGE',
    'persist/REGISTER',
    ...Object.keys(ExpandedKeysActionTypes),
    ...Object.keys(GlobalActionTypes),
];

export const reduxHashHistory = createBrowserHistory();

export const {createReduxHistory, routerMiddleware, routerReducer} = createReduxHistoryContext({
    history: reduxHashHistory,
    savePreviousLocations: 1
});

const createRootReducer = () =>
    combineReducers({
        router: routerReducer,
        system: systemReducer,
        ui: uiReducer,
        persisted: persistedReducer,
    });

export const rootReducer = (state: any, action: Action) => {
    const createdReducer = createRootReducer();

    if (action.type === GlobalActionTypes.LOGOUT) {
        return createdReducer({
                router: state.router,
                persisted: {global: defaultGlobalState, expandedKeys: defaultExpandedState},
                system: {
                    isFetching: false,
                    isPersisting: false,
                    savedChecksum: ""
                },
                ui: {
                    api: defaultAPIState,
                    status: defaultStatusState,
                    systemUserManagement: defaultSystemUserManagementState,
                    projectUserManagement: defaultProjectUserManagementState,
                },
            },
            action
        );
    }
    return createdReducer(state, action);
};

// Persisting the store in the session: on restart everything is lost, on page refresh the store is kept.
const createdPersistedReducer = persistReducer(persistConfig, rootReducer);
export const store: Store = createStore(
    createdPersistedReducer,
    applyMiddleware(reduxThunk, routerMiddleware, createStateSyncMiddleware({whitelist: actionWhiteList}))
);

export const history = createReduxHistory(store);

export type RootState = ReturnType<typeof rootReducer>;

export const useThunkDispatch = () => useDispatch<ThunkDispatch<RootState, undefined, UIActions>>();
